import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { useBooksStore } from "@/store/books";
import { api } from "@/api/client";
import {
  Cog6ToothIcon,
  UsersIcon,
  BookOpenIcon,
  PlusIcon,
  UserPlusIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  TrophyIcon,
  BoltIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import Icon from "@/components/Icon";

// DebugToolsPanel: Enhanced visual debugging for Capacitor/BarcodeScanner/App environment
const DebugToolsPanel: React.FC = () => {
  const [show, setShow] = useState(false);
  const [envInfo, setEnvInfo] = useState<any>({});
  const [permResult, setPermResult] = useState<any>(null);
  const [permRequestResult, setPermRequestResult] = useState<any>(null);

  const collectEnvInfo = async () => {
    const info: any = {};
    if (typeof window !== "undefined") {
      const cap = (window as any).Capacitor;
      info.Capacitor = cap || null;
      info.Capacitor_Platform =
        cap && typeof cap.getPlatform === "function"
          ? cap.getPlatform()
          : "N/A";
      info.isNative = !!(
        cap &&
        typeof cap.getPlatform === "function" &&
        cap.getPlatform() !== "web"
      );
      info.hasBarcodeScanner = !!cap?.Plugins?.BarcodeScanner;
      info.hasAppPlugin = !!cap?.Plugins?.App;
      info.navigatorUserAgent = window.navigator.userAgent;
      info.BarcodeScanner = cap?.Plugins?.BarcodeScanner || null;
      info.AppPlugin = cap?.Plugins?.App || null;

      // Try to get BarcodeScanner permissions and support
      if (info.hasBarcodeScanner) {
        try {
          if (
            typeof cap.Plugins.BarcodeScanner.checkPermissions === "function"
          ) {
            info.BarcodeScannerPermissions =
              await cap.Plugins.BarcodeScanner.checkPermissions();
          }
        } catch (e) {
          info.BarcodeScannerPermissions = { error: String(e) };
        }
        try {
          if (typeof cap.Plugins.BarcodeScanner.isSupported === "function") {
            info.BarcodeScannerSupported =
              await cap.Plugins.BarcodeScanner.isSupported();
          }
        } catch (e) {
          info.BarcodeScannerSupported = { error: String(e) };
        }
      }
    }
    setEnvInfo(info);
  };

  // Check permissions live
  const handleCheckPermissions = async () => {
    setPermResult(null);
    if (typeof window !== "undefined") {
      const cap = (window as any).Capacitor;
      if (cap?.Plugins?.BarcodeScanner?.checkPermissions) {
        try {
          const result = await cap.Plugins.BarcodeScanner.checkPermissions();
          setPermResult(result);
        } catch (e) {
          setPermResult({ error: String(e) });
        }
      } else {
        setPermResult({
          error: "BarcodeScanner.checkPermissions not available",
        });
      }
    }
  };

  // Request permissions live
  const handleRequestPermissions = async () => {
    setPermRequestResult(null);
    if (typeof window !== "undefined") {
      const cap = (window as any).Capacitor;
      if (cap?.Plugins?.BarcodeScanner?.requestPermissions) {
        try {
          const result = await cap.Plugins.BarcodeScanner.requestPermissions();
          setPermRequestResult(result);
        } catch (e) {
          setPermRequestResult({ error: String(e) });
        }
      } else {
        setPermRequestResult({
          error: "BarcodeScanner.requestPermissions not available",
        });
      }
    }
  };

  useEffect(() => {
    if (show) {
      collectEnvInfo();
    }
  }, [show]);

  return (
    <div className="mt-8">
      <button
        className="btn btn-outline btn-warning mb-2"
        onClick={() => setShow((v) => !v)}
        type="button"
      >
        {show ? "Hide Debug Tools" : "Show Debug Tools"}
      </button>
      {show && (
        <div className="bg-base-200 rounded-lg p-4 mt-2">
          <h3 className="font-bold mb-2 text-warning">Debug Tools</h3>
          <div className="mb-2">
            <strong>Capacitor Platform:</strong>{" "}
            <span className="badge badge-info">
              {envInfo.Capacitor_Platform}
            </span>
          </div>
          <div className="mb-2">
            <strong>isNative:</strong>{" "}
            <span className="badge badge-info">{String(envInfo.isNative)}</span>
          </div>
          <div className="mb-2">
            <strong>hasBarcodeScanner:</strong>{" "}
            <span className="badge badge-info">
              {String(envInfo.hasBarcodeScanner)}
            </span>
          </div>
          <div className="mb-2">
            <strong>hasAppPlugin:</strong>{" "}
            <span className="badge badge-info">
              {String(envInfo.hasAppPlugin)}
            </span>
          </div>
          <div className="mb-2">
            <strong>navigator.userAgent:</strong>
            <div className="text-xs break-all">
              {envInfo.navigatorUserAgent}
            </div>
          </div>
          {envInfo.BarcodeScannerSupported && (
            <div className="mb-2">
              <strong>BarcodeScanner.isSupported:</strong>{" "}
              <span className="badge badge-info">
                {JSON.stringify(envInfo.BarcodeScannerSupported)}
              </span>
            </div>
          )}
          {envInfo.BarcodeScannerPermissions && (
            <div className="mb-2">
              <strong>BarcodeScanner.checkPermissions:</strong>{" "}
              <span className="badge badge-info">
                {JSON.stringify(envInfo.BarcodeScannerPermissions)}
              </span>
            </div>
          )}
          {/* Live Permission Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              className="btn btn-xs btn-outline btn-info"
              type="button"
              onClick={handleCheckPermissions}
            >
              Check Permissions
            </button>
            <button
              className="btn btn-xs btn-outline btn-success"
              type="button"
              onClick={handleRequestPermissions}
            >
              Request Permissions
            </button>
            {/* Test Native Scan Button */}
            <button
              className="btn btn-xs btn-outline btn-error"
              type="button"
              onClick={async () => {
                const cap = (window as any).Capacitor;
                if (cap && cap.Plugins?.BarcodeScanner) {
                  try {
                    const perms =
                      await cap.Plugins.BarcodeScanner.checkPermissions();
                    if (perms.camera === "granted") {
                      const result = await cap.Plugins.BarcodeScanner.scan();
                      alert(JSON.stringify(result));
                    } else {
                      alert("Camera permission not granted");
                    }
                  } catch (err) {
                    alert("Scan error: " + JSON.stringify(err));
                  }
                } else {
                  alert("BarcodeScanner plugin not available");
                }
              }}
            >
              Test Native Scan
            </button>
          </div>
          {/* Show Permission Results */}
          {permResult && (
            <div className="mb-2">
              <strong>Live checkPermissions result:</strong>
              <button
                className="btn btn-xs btn-outline btn-info ml-2"
                type="button"
                onClick={() => {
                  const raw = JSON.stringify(permResult, null, 2);
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(raw);
                  } else {
                    const textarea = document.createElement("textarea");
                    textarea.value = raw;
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand("copy");
                    document.body.removeChild(textarea);
                  }
                }}
              >
                Copy
              </button>
              <pre className="text-xs overflow-x-auto">
                {JSON.stringify(permResult, null, 2)}
              </pre>
            </div>
          )}
          {permRequestResult && (
            <div className="mb-2">
              <strong>Live requestPermissions result:</strong>
              <button
                className="btn btn-xs btn-outline btn-info ml-2"
                type="button"
                onClick={() => {
                  const raw = JSON.stringify(permRequestResult, null, 2);
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(raw);
                  } else {
                    const textarea = document.createElement("textarea");
                    textarea.value = raw;
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand("copy");
                    document.body.removeChild(textarea);
                  }
                }}
              >
                Copy
              </button>
              <pre className="text-xs overflow-x-auto">
                {JSON.stringify(permRequestResult, null, 2)}
              </pre>
            </div>
          )}
          <details className="mt-2">
            <summary className="cursor-pointer">
              Show Raw Capacitor/BarcodeScanner/App Objects
            </summary>
            <div className="flex flex-col gap-2">
              <button
                className="btn btn-xs btn-outline btn-info w-32 mb-2"
                type="button"
                onClick={() => {
                  const raw = JSON.stringify(
                    {
                      Capacitor: envInfo.Capacitor,
                      BarcodeScanner: envInfo.BarcodeScanner,
                      AppPlugin: envInfo.AppPlugin,
                    },
                    null,
                    2
                  );
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(raw);
                  } else {
                    // fallback for older browsers
                    const textarea = document.createElement("textarea");
                    textarea.value = raw;
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand("copy");
                    document.body.removeChild(textarea);
                  }
                }}
              >
                Copy Debug Info
              </button>
              <pre className="text-xs overflow-x-auto">
                {JSON.stringify(
                  {
                    Capacitor: envInfo.Capacitor,
                    BarcodeScanner: envInfo.BarcodeScanner,
                    AppPlugin: envInfo.AppPlugin,
                  },
                  null,
                  2
                )}
              </pre>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

interface SystemStats {
  total_users: number;
  active_users: number;
  admin_users: number;
  total_books: number;
  new_users_30d: number;
  new_books_30d: number;
  top_users: Array<{ username: string; book_count: number }>;
}

interface AdminUser {
  id: number;
  username: string;
  email: string;
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
  book_count: number;
}

const AdminDashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const { books } = useBooksStore();
  const [stats, setStats] = useState<SystemStats>({
    total_users: 0,
    active_users: 0,
    admin_users: 0,
    total_books: 0,
    new_users_30d: 0,
    new_books_30d: 0,
    top_users: [],
  });
  const [recentUsers, setRecentUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch admin statistics
        const statsResponse = await api.admin.getStats();
        if (statsResponse.success && statsResponse.data) {
          setStats(statsResponse.data);
        }

        // Fetch recent users
        const usersResponse = await api.admin.getRecentUsers();
        if (usersResponse.success && usersResponse.data) {
          setRecentUsers(usersResponse.data);
        }
      } catch (err) {
        setError("Failed to load admin data");
        console.error("Admin data fetch error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAdminData();
  }, []);

  // Check if user is admin
  if (!user?.is_admin) {
    return (
      <div className="space-y-8">
        <div className="page-header px-6 py-5 mb-6">
          <h1 className="page-header-title">Access Denied</h1>
          <p className="page-header-subtitle">Admin privileges required</p>
        </div>

        <div className="bg-base-100 border-2 border-error/20 rounded-2xl p-8 shadow-lg text-center">
          <ExclamationTriangleIcon className="w-16 h-16 text-error mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-error mb-4">Access Denied</h2>
          <p className="text-base-content/70 mb-6">
            You do not have administrator privileges to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="btn btn-outline btn-error"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="page-header px-6 py-5 mb-6">
          <h1 className="page-header-title">Admin Dashboard</h1>
          <p className="page-header-subtitle">Loading system overview...</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="stat bg-base-100 rounded-box animate-pulse">
              <div className="stat-title bg-base-300 h-4 rounded mb-2"></div>
              <div className="stat-value bg-base-300 h-8 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div className="page-header px-6 py-5 mb-6">
          <h1 className="page-header-title">Error</h1>
          <p className="page-header-subtitle">{error}</p>
        </div>

        <div className="bg-base-100 border-2 border-error/20 rounded-2xl p-8 shadow-lg text-center">
          <h2 className="text-2xl font-bold text-error mb-4">
            Failed to Load Admin Dashboard
          </h2>
          <p className="text-base-content/70 mb-6">
            There was an error loading the admin data. Please try refreshing the
            page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-outline btn-error"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="page-header px-6 py-5 mb-6">
        <h1 className="page-header-title">Admin Dashboard</h1>
        <p className="page-header-subtitle">System overview and management</p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-2xl font-bold">System Overview</h2>
        <div className="flex gap-2">
          <Link to="/admin/users" className="btn btn-outline btn-primary">
            <Icon hero={<UsersIcon className="w-4 h-4" />} emoji="👥" />
            <span className="ml-2">Manage Users</span>
          </Link>
          <Link to="/admin/settings" className="btn btn-outline btn-secondary">
            <Icon hero={<Cog6ToothIcon className="w-4 h-4" />} emoji="⚙️" />
            <span className="ml-2">Settings</span>
          </Link>
          <Link to="/library" className="btn btn-outline btn-accent">
            <Icon hero={<BookOpenIcon className="w-4 h-4" />} emoji="📚" />
            <span className="ml-2">Back to Library</span>
          </Link>
        </div>
      </div>

      {/* System Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="stat bg-base-300 rounded-box">
          <div className="stat-title text-base-content/60">Total Users</div>
          <div className="stat-value text-3xl">{stats.total_users}</div>
          {stats.new_users_30d > 0 && (
            <div className="stat-desc text-base-content/60">
              +{stats.new_users_30d} this month
            </div>
          )}
        </div>

        <div className="stat bg-base-300 rounded-box">
          <div className="stat-title text-base-content/60">Active Users</div>
          <div className="stat-value text-3xl">{stats.active_users}</div>
          <div className="stat-desc text-base-content/60">
            {stats.admin_users} admin(s)
          </div>
        </div>

        <div className="stat bg-base-300 rounded-box">
          <div className="stat-title text-base-content/60">Total Books</div>
          <div className="stat-value text-3xl">{stats.total_books}</div>
          {stats.new_books_30d > 0 && (
            <div className="stat-desc text-base-content/60">
              +{stats.new_books_30d} this month
            </div>
          )}
        </div>

        <div className="stat bg-base-300 rounded-box">
          <div className="stat-title text-base-content/60">
            Avg Books/User
          </div>
          <div className="stat-value text-3xl">
            {stats.total_users > 0
              ? Math.round(stats.total_books / stats.total_users)
              : 0}
          </div>
          <div className="stat-desc text-base-content/60">
            System average
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card bg-base-100 shadow-xl mb-8">
        <div className="card-body">
          <h2 className="card-title text-primary mb-4 flex items-center gap-2">
            <Icon hero={<ChartBarIcon className="w-6 h-6" />} emoji="📊" />
            Recent Activity
          </h2>
          <div className="space-y-4">
            {recentUsers.length > 0 ? (
              recentUsers.slice(0, 3).map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 bg-base-200 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Icon
                      hero={<UserPlusIcon className="w-5 h-5" />}
                      emoji="➕"
                    />
                    <div>
                      <span className="font-semibold">
                        New User Registration
                      </span>
                      <p className="text-sm text-base-content/70">
                        User "{user.username}" registered
                      </p>
                    </div>
                  </div>
                  <span className="text-sm text-base-content/60">
                    {new Date(user.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="text-base-content/70">No recent user activity</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User and Book Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Top Users */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title text-primary mb-4 flex items-center gap-2">
              <Icon hero={<TrophyIcon className="w-6 h-6" />} emoji="🏆" />
              Most Active Users
            </h2>
            {stats.top_users.length > 0 ? (
              <div className="space-y-3">
                {stats.top_users.map((user, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center p-3 bg-base-200 rounded-lg"
                  >
                    <span className="font-semibold">{user.username}</span>
                    <span className="badge badge-primary">
                      {user.book_count} books
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-base-content/60">
                No user activity data available.
              </p>
            )}
          </div>
        </div>

        {/* Recent Users */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <div className="flex justify-between items-center mb-4">
              <h2 className="card-title text-primary flex items-center gap-2">
                <Icon hero={<UsersIcon className="w-6 h-6" />} emoji="👥" />
                Recent Users
              </h2>
              <span className="text-sm text-base-content/60">Last 30 days</span>
            </div>
            {recentUsers.length > 0 ? (
              <div className="space-y-3">
                {recentUsers.slice(0, 5).map((user) => (
                  <div
                    key={user.id}
                    className="flex justify-between items-start p-3 bg-base-200 rounded-lg"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">{user.username}</span>
                        {user.is_admin && (
                          <span className="badge badge-error">Admin</span>
                        )}
                      </div>
                      <p className="text-sm text-base-content/60">
                        {user.email}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-base-content/60 mb-1">
                        {new Date(user.created_at).toLocaleDateString()}
                      </p>
                      <Link
                        to={`/admin/users/${user.id}`}
                        className="btn btn-sm btn-outline btn-primary"
                      >
                        Manage
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-base-content/60">No recent users</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Books */}
      <div className="card bg-base-100 shadow-xl mb-8">
        <div className="card-body">
          <div className="flex justify-between items-center mb-6">
            <h2 className="card-title text-primary flex items-center gap-2">
              <Icon hero={<BookOpenIcon className="w-6 h-6" />} emoji="📚" />
              Recent Books
            </h2>
            <span className="text-sm text-base-content/60">Last 30 days</span>
          </div>

          {books.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {books.slice(0, 6).map((book) => (
                <div
                  key={book.uid}
                  className="flex items-center gap-3 p-3 bg-base-200 rounded-lg"
                >
                  <div className="w-12 h-16 bg-base-300 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={book.cover_url?.trim() || "/static/bookshelf.png"}
                      className="w-full h-full object-cover"
                      alt={`${book.title} cover`}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        // Prevent infinite loop by checking if we're already using the fallback
                        if (
                          target.src !==
                          window.location.origin + "/static/bookshelf.png"
                        ) {
                          target.src = "/static/bookshelf.png";
                        } else {
                          // If fallback also fails, hide the image and show a placeholder
                          target.style.display = "none";
                          const placeholder = document.createElement("div");
                          placeholder.className =
                            "w-full h-full bg-base-200 rounded flex items-center justify-center text-lg";
                          placeholder.innerHTML = "📚";
                          target.parentNode?.appendChild(placeholder);
                        }
                      }}
                    />
                  </div>
                  <div className="flex-grow min-w-0">
                    <h3 className="font-semibold truncate">{book.title}</h3>
                    <p className="text-sm text-base-content/70 truncate">
                      {book.author}
                    </p>
                    <div className="flex gap-1 mt-1">
                      {book.finish_date && (
                        <span className="badge badge-success badge-xs">
                          Finished
                        </span>
                      )}
                      {book.start_date && !book.finish_date && (
                        <span className="badge badge-info badge-xs">
                          Reading
                        </span>
                      )}
                      {book.want_to_read && (
                        <span className="badge badge-warning badge-xs">
                          Want to Read
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Icon
                hero={
                  <BookOpenIcon className="w-16 h-16 text-base-content/30 mx-auto mb-4" />
                }
                emoji="📚"
              />
              <p className="text-base-content/70">No books in the system yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title text-primary mb-6 flex items-center gap-2">
            <Icon hero={<BoltIcon className="w-6 h-6" />} emoji="⚡" />
            Quick Actions
          </h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/admin/users" className="btn btn-outline btn-primary">
              <Icon hero={<UsersIcon className="w-4 h-4" />} emoji="👥" />
              <span className="ml-2">Manage Users</span>
            </Link>
            <Link
              to="/admin/users/create"
              className="btn btn-outline btn-success"
            >
              <Icon hero={<UserPlusIcon className="w-4 h-4" />} emoji="➕" />
              <span className="ml-2">Create New User</span>
            </Link>
            <Link to="/import" className="btn btn-outline btn-info">
              <Icon hero={<PlusIcon className="w-4 h-4" />} emoji="📥" />
              <span className="ml-2">Bulk Import Books</span>
            </Link>
            <Link to="/admin/backup" className="btn btn-outline btn-warning">
              <Icon
                hero={<ArrowDownTrayIcon className="w-4 h-4" />}
                emoji="💾"
              />
              <span className="ml-2">Backup Database</span>
            </Link>
            <Link to="/admin/invites" className="btn btn-outline btn-secondary">
              <Icon hero={<EnvelopeIcon className="w-4 h-4" />} emoji="✉️" />
              <span className="ml-2">Manage Invites</span>
            </Link>
          </div>
        </div>
      </div>
      {/* Debug Tools Panel */}
      <DebugToolsPanel />
    </div>
  );
};

export default AdminDashboardPage;
